import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { CloseOutlined, SaveOutlined } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";

import CopyIconButton from "components/shared/CopyIconButton/CopyIconButton";
import { useCurrentUserRoleNames } from "components/RequirePermission/useAccessPermissions";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useUpdateRoleAssignmentsMutation } from "state/rtkQuery/access/accessMutations";
import { useAccessPlatformId } from "utils/accessManagement/useAccessPlatformId";
import { extractApiErrorMessage } from "utils/accessManagement/extractApiErrorMessage";
import {
  buildUpdateAssignmentPayload,
  canManageRole,
  getMyMaxRolePriority,
  isOrgExemptRole,
  isValidEmail,
} from "utils/accessManagement/roleAssignmentForm";
import { formatRoleLabel } from "utils/accessManagement/roleDisplay";
import { showToast } from "utils/reusableToast";

import "pages/ecrOps/OrgEditDrawer.css";

const ReadOnlyField = ({ label, value }) => (
  <TextField
    label={label}
    size="small"
    fullWidth
    value={value || "—"}
    InputProps={{
      readOnly: true,
      endAdornment: value ? <CopyIconButton value={value} label={`Copy ${label}`} /> : null,
    }}
    sx={{ "& .MuiInputBase-input": { color: "#6b7280" } }}
  />
);

// PUT /role-assignments always takes an array, even for a single edit — this
// drawer edits one record but builds the array-shaped payload via the shared builder
// so bulk edit can reuse it later without changing the payload contract.
const EditAssignmentDrawer = ({ assignment, roles, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [updateAssignments, { isLoading, isError, error, reset }] = useUpdateRoleAssignmentsMutation();
  // This platform's organizations only — same rule as AssignRoleDrawer.
  const { platformId } = useAccessPlatformId();
  const { data: activeOrgs } = useGetActiveOrganizationsQuery({ platformId }, { skip: !platformId });
  // "Assigned by" always reflects whoever is actually making this edit — it
  // is never a user-editable field (nor a preserved value from whoever
  // originally created the assignment), so there's no path in this UI where
  // it could be set to anyone other than the signed-in user.
  const currentUserEmail = useSelector((state) => state.auth?.userAccount?.username || "");
  // Role hierarchy — same rule and reasoning as AssignRoleDrawer: you can
  // only switch this assignment to a role at or below your own highest rank.
  const myRoleNames = useCurrentUserRoleNames();
  const myMaxPriority = getMyMaxRolePriority(myRoleNames, roles);

  useEffect(() => {
    if (assignment) {
      setForm({
        roleId: assignment.role_id,
        email: assignment.email || "",
        // PUT still takes ONE org_id string. An assignment that covers several orgs
        // can't be expressed that way, so for those the org is left out of the
        // update entirely (it stays as it is) instead of being overwritten.
        orgId: Array.isArray(assignment.org_id) ? (assignment.org_id.length === 1 ? assignment.org_id[0] : "") : assignment.org_id || "",
        assignedBy: currentUserEmail,
        isActive: assignment.is_active ?? true,
      });
      setErrors({});
      reset();
    }
  }, [assignment]); // eslint-disable-line

  if (!assignment || !form) return null;

  const set = (field) => (value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.roleId) e.roleId = "Role is required.";
    else {
      const targetRole = roles.find((r) => r.id === form.roleId);
      if (!canManageRole(targetRole, myMaxPriority)) {
        e.roleId = "You cannot assign a role higher than your own.";
      }
    }
    if (form.email && !isValidEmail(form.email)) e.email = "Enter a valid email address.";
    return e;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const result = await updateAssignments(
        buildUpdateAssignmentPayload({ id: assignment.id, ...form, platformId: assignment.platform_id || platformId })
      ).unwrap();

      if (result.not_updated?.length > 0) {
        showToast({
          title: "Update failed",
          description: result.not_updated[0]?.message || "The assignment could not be updated.",
          type: "error",
        });
        return;
      }

      showToast({ title: "Assignment updated", type: "success" });
      onSaved();
    } catch {
      // isError/error surface the failure inline; form state is preserved.
    }
  };

  const selectedRole = roles.find((r) => r.id === form.roleId) || null;
  const selectedOrg = activeOrgs?.find((org) => org._id === form.orgId) || null;
  const isOrgExempt = isOrgExemptRole(selectedRole?.role_name);
  const multiOrgIds = Array.isArray(assignment.org_id) && assignment.org_id.length > 1 ? assignment.org_id : null;

  return (
    <Drawer
      anchor="right"
      open={!!assignment}
      onClose={onClose}
      // Opened from a button inside the group-assignments Dialog — without an
      // explicit z-index above the theme's default modal layer (1300, shared
      // by every MUI Dialog/Drawer/Modal), this can render behind that
      // already-open Dialog instead of on top of it.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
    >
      <div className="org-drawer__header">
        <div className="org-drawer__header-text">
          <p className="org-drawer__header-eyebrow">Access Management</p>
          <h2 className="org-drawer__header-title">Edit assignment</h2>
          <code className="org-drawer__header-id">{assignment.id}</code>
        </div>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ color: "#9ca3af" }}>
            <CloseOutlined />
          </IconButton>
        </Tooltip>
      </div>

      <div className="org-drawer__body">
        <div className="org-drawer__section">
          <p className="org-drawer__section-title">Identity (read-only)</p>
          <div className="org-drawer__fields">
            {assignment.is_app ? (
              <ReadOnlyField label="Application ID" value={assignment.app_id} />
            ) : (
              <ReadOnlyField label="User ID" value={assignment.user_id} />
            )}
            <Alert severity="info" sx={{ fontSize: "12px" }}>
              To change the identity type, user ID, or application ID, create a replacement assignment and deactivate this one.
            </Alert>
          </div>
        </div>

        <Divider />

        <div className="org-drawer__section">
          <p className="org-drawer__section-title">Editable fields</p>
          <div className="org-drawer__fields">
            <Autocomplete
              options={roles}
              getOptionLabel={(role) => formatRoleLabel(role.role_name)}
              getOptionDisabled={(role) => role.is_active === false || !canManageRole(role, myMaxPriority)}
              value={selectedRole}
              onChange={(e, role) => {
                setForm((f) => ({
                  ...f,
                  roleId: role?.id || "",
                  orgId: isOrgExemptRole(role?.role_name) ? "" : f.orgId,
                }));
                setErrors((err) => ({ ...err, roleId: undefined }));
              }}
              renderOption={(props, role) => {
                const isTooHighRank = role.is_active !== false && !canManageRole(role, myMaxPriority);
                return (
                  <li {...props} key={role.id} title={isTooHighRank ? "You cannot assign a role higher than your own." : undefined}>
                    {formatRoleLabel(role.role_name)}
                    {role.is_active === false && " (inactive)"}
                    {isTooHighRank && " (higher than your role)"}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Role" required size="small" error={!!errors.roleId} helperText={errors.roleId} />
              )}
            />
            {!assignment.is_app && (
              <TextField
                label="Email"
                size="small"
                fullWidth
                value={form.email}
                onChange={(e) => set("email")(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
              />
            )}
            {!isOrgExempt && multiOrgIds && (
              <TextField
                label="Organizations"
                size="small"
                fullWidth
                value={multiOrgIds.map((id) => activeOrgs?.find((org) => org._id === id)?.name || id).join(", ")}
                InputProps={{ readOnly: true }}
                helperText={`This assignment covers ${multiOrgIds.length} organizations. Its organizations can't be changed here — they stay as they are.`}
              />
            )}
            {!isOrgExempt && !multiOrgIds && (
              <Autocomplete
                options={activeOrgs || []}
                getOptionLabel={(org) => org.name || ""}
                value={selectedOrg}
                onChange={(e, org) => set("orgId")(org?._id || "")}
                renderInput={(params) => (
                  <TextField {...params} label="Organization" size="small" error={!!errors.orgId} helperText={errors.orgId} />
                )}
              />
            )}
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => set("isActive")(e.target.checked)} size="small" />}
              label={form.isActive ? "Active" : "Inactive"}
            />
          </div>
        </div>

        {isError && (
          <div className="org-drawer__section">
            <Alert severity="error" sx={{ fontSize: "13px" }}>
              {extractApiErrorMessage(error, "Failed to update role assignment. Please try again.")}
            </Alert>
          </div>
        )}
      </div>

      <div className="org-drawer__footer">
        <button type="button" className="org-drawer__btn org-drawer__btn--cancel" onClick={onClose} disabled={isLoading}>
          Cancel
        </button>
        <button
          type="button"
          className="org-drawer__btn org-drawer__btn--save"
          onClick={(event) => {
            // Stacked on top of the group-assignments Dialog — without
            // stopping propagation, React's delegated event system can let
            // this same click bubble out to the OUTER Dialog's
            // backdrop-click-away detection after this drawer closes
            // mid-click, closing the group modal behind it too.
            event.stopPropagation();
            handleSave();
          }}
          disabled={isLoading}
        >
          {isLoading ? "Saving…" : (<><SaveOutlined style={{ fontSize: 16, marginRight: 6 }} />Save changes</>)}
        </button>
      </div>
    </Drawer>
  );
};

EditAssignmentDrawer.propTypes = {
  assignment: PropTypes.object,
  roles: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default EditAssignmentDrawer;
